import React from "react";
import styled from "styled-components";

type TextButtonVariant = "link" | "action";

interface TextButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: TextButtonVariant;
  children: React.ReactNode;
  icon?: React.ReactNode;
  inline?: boolean;
}

const InlineSpan = styled.span<{ $variant: TextButtonVariant }>(
  ({ theme, $variant }) => ({
    color:
      $variant === "link"
        ? theme.kallisto.color.text.static.textPrimary
        : theme.kallisto.color.text.static.textSuccess,
    textDecoration: $variant === "link" ? "underline" : "none",
    cursor: "pointer",
    "button:hover > &": {
      color: theme.kallisto.color.text.interactive.textPrimaryHover,
    },
    "button:active > &": {
      color: theme.kallisto.color.text.interactive.textPrimaryPressed,
    },
  })
);

const StyledButton = styled.button<{
  $variant: TextButtonVariant;
  $hasIcon: boolean;
  $inline: boolean;
}>(({ theme, $variant, $hasIcon, $inline }) => ({
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  fontFamily: theme.kallisto.font.family.sans,
  fontSize: theme.kallisto.font.size[175],

  ...(!$inline && {
    color:
      $variant === "link"
        ? theme.kallisto.color.text.static.textPrimary
        : theme.kallisto.color.text.static.textSuccess,
    textDecoration: $variant === "link" ? "underline" : "none",
  }),
  fontWeight:
    $variant === "action" ? theme.kallisto.font.weight.bold : "normal",

  ...($inline && {
    display: "contents",
    fontSize: "inherit",
    fontFamily: "inherit",
  }),
  ...(($variant === "action" || $hasIcon) && !$inline && {
    display: "flex",
    alignItems: "center",
    gap: theme.kallisto.space.space50,
  }),

  "& svg": {
    color: "currentColor",
  },

  "&:hover": {
    color: theme.kallisto.color.text.interactive.textPrimaryHover,
  },
  "&:active": {
    color: theme.kallisto.color.text.interactive.textPrimaryPressed,
  },
  "&:focus": {
    color: theme.kallisto.color.text.interactive.textPrimaryPressed,
  },
  "&:disabled": {
    cursor: "not-allowed",
    color: theme.kallisto.color.text.static.textDisabled,
  },
  "&:focus-visible": {
    outline: `2px ${theme.kallisto.color.common.white[1000]} solid`,
    outlineOffset: 0,
    boxShadow: `0 0 0 4px ${theme.kallisto.color.common.black[1000]}`,
  },
})
);

const TextButton: React.FC<TextButtonProps> = ({
  variant = "link",
  children,
  icon,
  inline = false,
  ...props
}) => {
  return (
    <StyledButton $variant={variant} $hasIcon={!!icon} $inline={inline} type="button" {...props}>
      {icon}
      {inline ? (
        <InlineSpan $variant={variant}>{children}</InlineSpan>
      ) : (
        children
      )}
    </StyledButton>
  );
};

export default TextButton;
